import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Paper,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  Pagination,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { Colour } from '../../api/jerseyApi';
import { ColorListState, ColorFormData } from '../../types/color';
import { jerseyApi } from '../../api/jerseyApi';
import ColorCard from './ColorCard';
import ColorForm from './ColorForm';

const ColorList: React.FC = () => {
  const [state, setState] = useState<ColorListState>({
    colors: [],
    loading: false,
    error: null,
    filters: {
      search: '',
    },
    pagination: {
      page: 1,
      limit: 12,
      total: 0,
    },
  });

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState<Colour | undefined>(undefined);
  const [formLoading, setFormLoading] = useState(false);

  // Load colors
  const loadColors = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const params = {
        skip: (state.pagination.page - 1) * state.pagination.limit,
        limit: state.pagination.limit,
        search: state.filters.search || undefined,
      };

      const response = await jerseyApi.listColors(params);

      setState(prev => ({
        ...prev,
        colors: response.items,
        loading: false,
        pagination: {
          page: response.page,
          limit: response.size,
          total: response.total,
        },
      }));
    } catch (error) {
      console.error('Error loading colors:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load colors. Please try again.',
      }));
    }
  }, [state.pagination.page, state.pagination.limit, state.filters.search]);

  // Load colors on component mount and when dependencies change
  useEffect(() => {
    loadColors();
  }, [loadColors]);

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const search = event.target.value;
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, search },
      pagination: { ...prev.pagination, page: 1 }, // Reset to first page
    }));
  };

  // Handle pagination
  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setState(prev => ({
      ...prev,
      pagination: { ...prev.pagination, page },
    }));
  };

  // Handle refresh
  const handleRefresh = () => {
    loadColors();
  };

  // Handle create new color
  const handleCreateNew = () => {
    setSelectedColor(undefined);
    setFormDialogOpen(true);
  };

  // Handle edit color
  const handleEditColor = (color: Colour) => {
    setSelectedColor(color);
    setFormDialogOpen(true);
  };

  // Handle delete color
  const handleDeleteColor = async (color: Colour) => {
    try {
      await jerseyApi.deleteColor(color.id);
      loadColors(); // Refresh the list
    } catch (error) {
      console.error('Error deleting color:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to delete color. Please try again.',
      }));
    }
  };

  // Handle form submit
  const handleFormSubmit = async (data: ColorFormData) => {
    setFormLoading(true);
    
    try {
      if (selectedColor) {
        // Update existing color
        await jerseyApi.updateColor(selectedColor.id, data);
      } else {
        // Create new color
        await jerseyApi.createColor(data);
      }
      
      setFormDialogOpen(false);
      setSelectedColor(undefined);
      loadColors(); // Refresh the list
    } catch (error) {
      console.error('Error saving color:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to save color. Please try again.',
      }));
    } finally {
      setFormLoading(false);
    }
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setFormDialogOpen(false);
    setSelectedColor(undefined);
  };

  const totalPages = Math.ceil(state.pagination.total / state.pagination.limit);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaletteIcon color="primary" />
          <Chip
            label={`${state.pagination.total} colors`}
            size="small"
            variant="outlined"
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          size="large"
        >
          Add Color
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search colors by name..."
              value={state.filters.search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={state.loading}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {state.error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setState(prev => ({ ...prev, error: null }))}>
          {state.error}
        </Alert>
      )}

      {/* Loading State */}
      {state.loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Colors Grid */}
      {!state.loading && (
        <>
          {state.colors.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <PaletteIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No colors found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {state.filters.search 
                  ? 'Try adjusting your search criteria or create a new color.'
                  : 'Get started by creating your first color.'
                }
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
              >
                Create First Color
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {state.colors.map((color) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={color.id}>
                  <ColorCard
                    color={color}
                    onEdit={handleEditColor}
                    onDelete={handleDeleteColor}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={state.pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* Floating Action Button for Mobile */}
      <Fab
        color="primary"
        aria-label="add color"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', md: 'none' },
        }}
        onClick={handleCreateNew}
      >
        <AddIcon />
      </Fab>

      {/* Color Form Dialog */}
      <Dialog
        open={formDialogOpen}
        onClose={handleFormCancel}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle>
          {selectedColor ? 'Edit Color' : 'Create New Color'}
        </DialogTitle>
        <DialogContent>
          <ColorForm
            color={selectedColor}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            loading={formLoading}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ColorList;
